import { useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Upload, FileSpreadsheet, Play, CheckCircle2, XCircle, AlertTriangle, Info, Loader2, RefreshCw, ChevronDown, ChevronRight, Building2 } from 'lucide-react';
import { useDataCache } from '@/hooks/useDataCache';

interface CsvRow {
  referencia: string;
  concepto: string;
  cantidad: number;
  fecha: string;
}

interface ParseResult {
  rows: CsvRow[];
  isBankFormat: boolean;
  skipped: number;
  depositTotal: number;
}

interface PagoAplicado {
  invoiceId: number;
  invoiceNumber: string;
  amountApplied: number;
  invoiceStatus: string;
}

interface ResultRow extends CsvRow {
  status: 'ok' | 'parcial' | 'sin_facturas' | 'error';
  message: string;
  alumno: string | null;
  producto: string | null;
  pagosAplicados: PagoAplicado[];
}

interface BankAccount {
  id: number;
  name: string;
  code: string;
}

const STATUS_CONFIG = {
  ok: { label: 'Registrado', color: 'bg-green-100 text-green-800', icon: CheckCircle2 },
  parcial: { label: 'Parcial', color: 'bg-yellow-100 text-yellow-800', icon: AlertTriangle },
  sin_facturas: { label: 'Sin facturas', color: 'bg-blue-100 text-blue-800', icon: Info },
  error: { label: 'Error', color: 'bg-red-100 text-red-800', icon: XCircle },
};

function fmt(n: number) {
  return new Intl.NumberFormat('es-MX', { style: 'currency', currency: 'MXN' }).format(n);
}

// ── CSV helpers ──────────────────────────────────────────────────────────────

/**
 * Proper CSV line parser that handles quoted fields containing commas.
 * Handles the bank's format: "5,150.00" stays as one field.
 */
function parseCSVLine(line: string): string[] {
  const result: string[] = [];
  let current = '';
  let inQuotes = false;
  for (let i = 0; i < line.length; i++) {
    const ch = line[i];
    if (ch === '"') {
      inQuotes = !inQuotes;
    } else if (ch === ',' && !inQuotes) {
      result.push(current);
      current = '';
    } else {
      current += ch;
    }
  }
  result.push(current);
  return result;
}

/**
 * Strip leading apostrophes (Excel artifact: '01092025 → 01092025),
 * surrounding quotes, and extra whitespace.
 */
function cleanField(s: string): string {
  return s.replace(/^['\s]+/, '').replace(/['\s]+$/, '').replace(/^"+|"+$/g, '').trim();
}

/**
 * Parse a Mexican bank amount: "1,500.00" → 1500, "0" → 0
 */
function parseAmount(s: string): number {
  const cleaned = cleanField(s).replace(/,/g, '');
  const n = parseFloat(cleaned);
  return isNaN(n) ? 0 : n;
}

/**
 * Convert DDMMYYYY (bank format) to YYYY-MM-DD.
 * Leaves other formats unchanged.
 */
function parseBankDate(s: string): string {
  const raw = cleanField(s);
  if (/^\d{8}$/.test(raw)) {
    const dd = raw.slice(0, 2);
    const mm = raw.slice(2, 4);
    const yyyy = raw.slice(4, 8);
    return `${yyyy}-${mm}-${dd}`;
  }
  // Already in YYYY-MM-DD or other readable format — return as-is
  return raw;
}

// ── Main parser ──────────────────────────────────────────────────────────────

function parseCsv(text: string): ParseResult {
  const lines = text.split('\n').map(l => l.trimEnd()).filter(l => l.trim().length > 0);
  if (lines.length < 2) throw new Error('El archivo CSV debe tener encabezado y al menos una fila de datos');

  // ── Detect bank format ───────────────────────────────────────────────────
  // Scan first 15 lines for the bank header row (contains "Importe Abono" or "Importe Cargo")
  let bankHeaderIdx = -1;
  for (let i = 0; i < Math.min(lines.length, 15); i++) {
    const lower = lines[i].toLowerCase();
    if (lower.includes('importe abono') || lower.includes('importe cargo')) {
      bankHeaderIdx = i;
      break;
    }
  }

  if (bankHeaderIdx >= 0) {
    // ═══════════════════════════════════════════════════════════════════════
    // BANK FORMAT
    // Header: Fecha, Hora, Sucursal, Descripcion, Importe Cargo, Importe Abono,
    //         Saldo, Referencia, Concepto
    // ═══════════════════════════════════════════════════════════════════════
    const headerCols = parseCSVLine(lines[bankHeaderIdx]).map(h => cleanField(h).toLowerCase());

    const idxFecha  = headerCols.findIndex(h => h === 'fecha');
    const idxCargo  = headerCols.findIndex(h => h.includes('importe cargo'));
    const idxAbono  = headerCols.findIndex(h => h.includes('importe abono'));
    const idxRef    = headerCols.findIndex(h => h === 'referencia');
    const idxConc   = headerCols.findIndex(h => h === 'concepto');
    const idxDesc   = headerCols.findIndex(h => h === 'descripcion');

    if (idxAbono < 0) throw new Error('Columna "Importe Abono" no encontrada en el encabezado del banco');
    if (idxFecha < 0) throw new Error('Columna "Fecha" no encontrada en el encabezado del banco');

    const rows: CsvRow[] = [];
    let skipped = 0;
    let depositTotal = 0;

    for (let i = bankHeaderIdx + 1; i < lines.length; i++) {
      const line = lines[i].trim();
      if (!line) continue;

      const cols = parseCSVLine(line);

      const abono = idxAbono >= 0 ? parseAmount(cols[idxAbono] ?? '0') : 0;
      const cargo = idxCargo >= 0 ? parseAmount(cols[idxCargo] ?? '0') : 0;

      // Only process credits (deposits): abono > 0 AND no simultaneous debit
      if (abono <= 0 || cargo > 0) {
        skipped++;
        continue;
      }

      const fechaStr = parseBankDate(cols[idxFecha] ?? '');
      if (!fechaStr) { skipped++; continue; }

      const referencia = idxRef >= 0 ? cleanField(cols[idxRef] ?? '') : '';
      const descripcion = idxDesc >= 0 ? cleanField(cols[idxDesc] ?? '') : '';
      const concepto   = idxConc >= 0 ? cleanField(cols[idxConc] ?? '') : '';

      // Build a combined description: "AB TRANSF SPEI | Priscila Mayorga Silva..."
      const fullConcepto = [descripcion, concepto].filter(Boolean).join(' | ');

      depositTotal += abono;
      rows.push({
        referencia: referencia || '—',
        concepto: fullConcepto,
        cantidad: abono,
        fecha: fechaStr,
      });
    }

    if (rows.length === 0)
      throw new Error('No se encontraron depósitos (abonos) en el estado de cuenta. Verifica que el archivo sea el correcto.');

    return { rows, isBankFormat: true, skipped, depositTotal };
  }

  // ═══════════════════════════════════════════════════════════════════════════
  // LEGACY FORMAT (referencia, concepto, cantidad, fecha)
  // ═══════════════════════════════════════════════════════════════════════════
  const sep = lines[0].includes(';') ? ';' : ',';
  const headers = lines[0].split(sep).map(h => h.trim().toLowerCase().replace(/[^a-záéíóúñ0-9_]/gi, ''));

  const idxRef  = headers.findIndex(h => h.includes('referencia') || h === 'ref');
  const idxConc = headers.findIndex(h => h.includes('concepto') || h.includes('descripcion') || h.includes('detalle') || h.includes('nota'));
  const idxCant = headers.findIndex(h => h.includes('cantidad') || h.includes('monto') || h.includes('importe') || h.includes('pago'));
  const idxFecha = headers.findIndex(h => h.includes('fecha') || h.includes('date'));

  if (idxRef < 0)  throw new Error('Columna "referencia" no encontrada en el CSV');
  if (idxCant < 0) throw new Error('Columna "cantidad" / "monto" no encontrada en el CSV');
  if (idxFecha < 0) throw new Error('Columna "fecha" no encontrada en el CSV');

  const rows: CsvRow[] = [];
  let depositTotal = 0;
  for (let i = 1; i < lines.length; i++) {
    const cols = lines[i].split(sep).map(c => c.trim().replace(/^"|"$/g, ''));
    const referencia   = cols[idxRef] || '';
    const concepto     = idxConc >= 0 ? (cols[idxConc] || '') : '';
    const cantidadRaw  = (cols[idxCant] || '0').replace(/[$,\s]/g, '');
    const cantidad     = parseFloat(cantidadRaw);
    const fecha        = cols[idxFecha] || '';

    if (!referencia || isNaN(cantidad) || cantidad <= 0 || !fecha) continue;
    depositTotal += cantidad;
    rows.push({ referencia, concepto, cantidad, fecha });
  }

  if (rows.length === 0) throw new Error('No se encontraron filas válidas en el CSV');
  return { rows, isBankFormat: false, skipped: 0, depositTotal };
}

// ── Component ────────────────────────────────────────────────────────────────

export default function BulkCsvPayment() {
  const { cachedData } = useDataCache();
  const fileRef = useRef<HTMLInputElement>(null);
  const [parseResult, setParseResult] = useState<ParseResult | null>(null);
  const [results, setResults] = useState<ResultRow[]>([]);
  const [bankAccountId, setBankAccountId] = useState<number | null>(null);
  const [bankAccounts, setBankAccounts] = useState<BankAccount[]>([]);
  const [loadingAccounts, setLoadingAccounts] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [parseError, setParseError] = useState<string | null>(null);
  const [fileName, setFileName] = useState<string | null>(null);
  const [expandedRows, setExpandedRows] = useState<Set<number>>(new Set());

  const rows = parseResult?.rows ?? [];

  const loadBankAccounts = async () => {
    setLoadingAccounts(true);
    try {
      const res = await fetch('/api/administration/payment-accounts', { credentials: 'include' });
      if (res.ok) {
        const data = await res.json();
        setBankAccounts(data);
        if (data.length === 1) setBankAccountId(data[0].id);
      }
    } catch (e) {
      console.error('Error cargando cuentas:', e);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleFile = (file: File) => {
    setParseError(null);
    setResults([]);
    setFileName(file.name);

    // Try UTF-8 first; fall back to Latin-1 if the file has non-UTF-8 chars
    const tryParse = (encoding: string) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const result = parseCsv(text);
          setParseResult(result);
          if (bankAccounts.length === 0) loadBankAccounts();
        } catch (err: any) {
          if (encoding === 'UTF-8') {
            tryParse('ISO-8859-1');
          } else {
            setParseError(err.message);
            setParseResult(null);
          }
        }
      };
      reader.readAsText(file, encoding);
    };
    tryParse('UTF-8');
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    const file = e.dataTransfer.files[0];
    if (file && (file.name.endsWith('.csv') || file.type === 'text/csv')) {
      handleFile(file);
    }
  };

  const handleProcess = async () => {
    if (!bankAccountId || rows.length === 0) return;
    setProcessing(true);
    setResults([]);
    try {
      const res = await fetch('/api/treasury/payments/bulk-csv', {
        method: 'POST',
        credentials: 'include',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ bankAccountId, rows }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Error del servidor');
      setResults(data.results || []);
    } catch (err: any) {
      setParseError(err.message);
    } finally {
      setProcessing(false);
    }
  };

  const reset = () => {
    setParseResult(null);
    setResults([]);
    setFileName(null);
    setParseError(null);
    if (fileRef.current) fileRef.current.value = '';
  };

  const toggleExpand = (i: number) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(i) ? next.delete(i) : next.add(i);
      return next;
    });
  };

  const summary = results.length > 0 ? {
    ok: results.filter(r => r.status === 'ok').length,
    parcial: results.filter(r => r.status === 'parcial').length,
    sinFacturas: results.filter(r => r.status === 'sin_facturas').length,
    error: results.filter(r => r.status === 'error').length,
    total: results.reduce((s, r) => s + r.pagosAplicados.reduce((a, p) => a + p.amountApplied, 0), 0),
  } : null;

  const isBankFormat = parseResult?.isBankFormat ?? false;
  const [showAllPreview, setShowAllPreview] = useState(false);
  const PREVIEW_LIMIT = 100;

  return (
    <div className="space-y-5">
      {/* Format guide */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Alert>
          <Building2 className="h-4 w-4" />
          <AlertDescription>
            <strong>Estado de cuenta bancario (automático):</strong> Sube directamente el CSV que genera tu banco. El sistema detecta el formato con las columnas <code className="bg-muted px-1 rounded text-xs">Fecha, Descripcion, Importe Cargo, Importe Abono, Referencia, Concepto</code> y procesa solo los abonos. La <strong>Referencia</strong> que el alumno anota en su transferencia debe tener 7 dígitos: primeros 4 = número de NUI, últimos 3 = SKU del producto.
          </AlertDescription>
        </Alert>
        <Alert>
          <Info className="h-4 w-4" />
          <AlertDescription>
            <strong>Formato propio (alternativo):</strong> CSV con columnas <code className="bg-muted px-1 rounded text-xs">referencia, concepto, cantidad, fecha</code>. La <strong>referencia</strong> es el mismo código de 7 dígitos (NUI + SKU). El método de pago siempre es <strong>Transferencia</strong>; distribución en <strong>cascada</strong> (factura más antigua primero).
          </AlertDescription>
        </Alert>
      </div>

      {/* Upload zone */}
      {!parseResult && !parseError && (
        <div
          className="border-2 border-dashed border-muted-foreground/30 rounded-xl p-10 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors"
          onDrop={handleDrop}
          onDragOver={e => e.preventDefault()}
          onClick={() => fileRef.current?.click()}
        >
          <FileSpreadsheet className="h-12 w-12 mx-auto mb-3 text-muted-foreground" />
          <p className="font-medium text-lg mb-1">Arrastra tu archivo CSV aquí</p>
          <p className="text-sm text-muted-foreground mb-1">Estado de cuenta bancario o formato propio</p>
          <p className="text-xs text-muted-foreground mb-4">Compatible con Santander, BBVA, Banamex y otros bancos mexicanos</p>
          <Button variant="outline" size="sm" onClick={e => { e.stopPropagation(); fileRef.current?.click(); }}>
            <Upload className="h-4 w-4 mr-2" />
            Seleccionar archivo
          </Button>
          <input ref={fileRef} type="file" accept=".csv,text/csv" className="hidden" onChange={e => { const f = e.target.files?.[0]; if (f) handleFile(f); }} />
        </div>
      )}

      {parseError && (
        <Alert variant="destructive">
          <XCircle className="h-4 w-4" />
          <AlertDescription>
            {parseError}
            <button onClick={reset} className="ml-3 underline text-xs">Intentar con otro archivo</button>
          </AlertDescription>
        </Alert>
      )}

      {/* Preview + config */}
      {rows.length > 0 && results.length === 0 && (
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between flex-wrap gap-2">
              <CardTitle className="flex items-center gap-2 text-base flex-wrap">
                <FileSpreadsheet className="h-5 w-5 text-primary shrink-0" />
                <span className="font-normal text-muted-foreground truncate max-w-[200px]">{fileName}</span>
                {isBankFormat ? (
                  <Badge className="bg-blue-100 text-blue-800 border-0">Estado de cuenta bancario</Badge>
                ) : (
                  <Badge variant="secondary">Formato propio</Badge>
                )}
                <Badge variant="outline">{rows.length} abono{rows.length !== 1 ? 's' : ''}</Badge>
                {parseResult && parseResult.skipped > 0 && (
                  <Badge variant="outline" className="text-orange-600 border-orange-300">
                    {parseResult.skipped} cargos omitidos
                  </Badge>
                )}
              </CardTitle>
              <Button variant="ghost" size="sm" onClick={reset}>
                <RefreshCw className="h-4 w-4 mr-1" /> Cargar otro
              </Button>
            </div>

            {/* Deposit total summary */}
            {parseResult && parseResult.depositTotal > 0 && (
              <div className="flex items-center gap-2 bg-green-50 border border-green-200 rounded-lg px-4 py-2 text-sm w-fit">
                <CheckCircle2 className="h-4 w-4 text-green-600" />
                <span className="text-green-800">Total de abonos a procesar:</span>
                <span className="font-bold text-green-700">{fmt(parseResult.depositTotal)}</span>
              </div>
            )}
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="overflow-x-auto rounded border">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted">
                    <th className="text-left px-3 py-2">#</th>
                    <th className="text-left px-3 py-2">Fecha</th>
                    <th className="text-left px-3 py-2">Referencia</th>
                    <th className="text-left px-3 py-2">{isBankFormat ? 'Descripción | Concepto' : 'Concepto'}</th>
                    <th className="text-right px-3 py-2">Abono</th>
                  </tr>
                </thead>
                <tbody>
                  {(showAllPreview ? rows : rows.slice(0, PREVIEW_LIMIT)).map((r, i) => (
                    <tr key={i} className="border-t">
                      <td className="px-3 py-1.5 text-muted-foreground text-xs">{i + 1}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{r.fecha}</td>
                      <td className="px-3 py-1.5 font-mono text-xs">{r.referencia}</td>
                      <td className="px-3 py-1.5 text-xs text-muted-foreground max-w-[240px] truncate" title={r.concepto}>
                        {r.concepto || '—'}
                      </td>
                      <td className="px-3 py-1.5 text-right text-green-700 font-semibold">{fmt(r.cantidad)}</td>
                    </tr>
                  ))}
                  {rows.length > PREVIEW_LIMIT && !showAllPreview && (
                    <tr className="border-t bg-muted/50">
                      <td colSpan={5} className="px-3 py-2 text-center text-xs">
                        <button
                          className="text-primary underline hover:no-underline"
                          onClick={() => setShowAllPreview(true)}
                        >
                          Ver las {rows.length - PREVIEW_LIMIT} filas restantes (total: {rows.length})
                        </button>
                      </td>
                    </tr>
                  )}
                  {showAllPreview && rows.length > PREVIEW_LIMIT && (
                    <tr className="border-t bg-muted/50">
                      <td colSpan={5} className="px-3 py-2 text-center text-xs">
                        <button
                          className="text-muted-foreground underline hover:no-underline"
                          onClick={() => setShowAllPreview(false)}
                        >
                          Mostrar solo las primeras {PREVIEW_LIMIT} filas
                        </button>
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Bank account selector */}
            <div className="flex flex-col sm:flex-row gap-3 items-start sm:items-end">
              <div className="flex-1">
                <label className="text-sm font-medium mb-1 block">Cuenta bancaria destino (donde llegaron los abonos)</label>
                {loadingAccounts ? (
                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Loader2 className="h-4 w-4 animate-spin" /> Cargando cuentas...
                  </div>
                ) : (
                  <select
                    className="w-full border rounded-md px-3 py-2 text-sm bg-background"
                    value={bankAccountId ?? ''}
                    onChange={e => setBankAccountId(Number(e.target.value))}
                  >
                    <option value="">-- Seleccionar cuenta --</option>
                    {bankAccounts.map(a => (
                      <option key={a.id} value={a.id}>{a.code} — {a.name}</option>
                    ))}
                  </select>
                )}
              </div>
              <Button
                className="sm:self-end"
                disabled={!bankAccountId || processing}
                onClick={handleProcess}
              >
                {processing ? (
                  <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Procesando {rows.length} abonos...</>
                ) : (
                  <><Play className="h-4 w-4 mr-2" /> Procesar {rows.length} abono{rows.length !== 1 ? 's' : ''}</>
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Results */}
      {results.length > 0 && (
        <div className="space-y-4">
          {/* Summary cards */}
          {summary && (
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
              <Card className="p-3 text-center bg-green-50">
                <div className="text-2xl font-bold text-green-700">{summary.ok}</div>
                <div className="text-xs text-green-600">Registrados</div>
              </Card>
              <Card className="p-3 text-center bg-yellow-50">
                <div className="text-2xl font-bold text-yellow-700">{summary.parcial}</div>
                <div className="text-xs text-yellow-600">Parciales</div>
              </Card>
              <Card className="p-3 text-center bg-blue-50">
                <div className="text-2xl font-bold text-blue-700">{summary.sinFacturas}</div>
                <div className="text-xs text-blue-600">Sin facturas</div>
              </Card>
              <Card className="p-3 text-center bg-red-50">
                <div className="text-2xl font-bold text-red-700">{summary.error}</div>
                <div className="text-xs text-red-600">Errores / No identificados</div>
              </Card>
              <Card className="p-3 text-center bg-primary/10">
                <div className="text-lg font-bold text-primary">{fmt(summary.total)}</div>
                <div className="text-xs text-muted-foreground">Total aplicado</div>
              </Card>
            </div>
          )}

          <Card>
            <CardHeader>
              <div className="flex items-center justify-between">
                <CardTitle className="text-base">Resultados del procesamiento</CardTitle>
                <Button variant="ghost" size="sm" onClick={reset}>
                  <RefreshCw className="h-4 w-4 mr-1" /> Nuevo lote
                </Button>
              </div>
            </CardHeader>
            <CardContent>
              <div className="overflow-x-auto rounded border">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="bg-muted">
                      <th className="text-center px-3 py-2 w-8"></th>
                      <th className="text-left px-3 py-2">Fecha</th>
                      <th className="text-left px-3 py-2">Referencia</th>
                      <th className="text-left px-3 py-2">Alumno</th>
                      <th className="text-left px-3 py-2">Producto</th>
                      <th className="text-left px-3 py-2">Descripción</th>
                      <th className="text-right px-3 py-2">Abono</th>
                      <th className="text-left px-3 py-2">Estado</th>
                      <th className="text-left px-3 py-2">Mensaje</th>
                    </tr>
                  </thead>
                  <tbody>
                    {results.map((r, i) => {
                      const cfg = STATUS_CONFIG[r.status] || STATUS_CONFIG.error;
                      const Icon = cfg.icon;
                      const isExpanded = expandedRows.has(i);
                      const hasSubs = r.pagosAplicados.length > 0;
                      return (
                        <>
                          <tr key={`row-${i}`} className={`border-t ${isExpanded ? 'bg-muted/40' : 'hover:bg-muted/20'}`}>
                            <td className="px-3 py-2 text-center">
                              {hasSubs ? (
                                <button onClick={() => toggleExpand(i)} className="text-muted-foreground hover:text-foreground">
                                  {isExpanded ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                                </button>
                              ) : null}
                            </td>
                            <td className="px-3 py-2 font-mono text-xs">{r.fecha}</td>
                            <td className="px-3 py-2 font-mono text-xs">{r.referencia}</td>
                            <td className="px-3 py-2 text-xs">{r.alumno || '—'}</td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">{r.producto || '—'}</td>
                            <td className="px-3 py-2 text-muted-foreground text-xs max-w-[180px] truncate" title={r.concepto}>
                              {r.concepto || '—'}
                            </td>
                            <td className="px-3 py-2 text-right font-medium">{fmt(r.cantidad)}</td>
                            <td className="px-3 py-2">
                              <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${cfg.color}`}>
                                <Icon className="h-3 w-3" />
                                {cfg.label}
                              </span>
                            </td>
                            <td className="px-3 py-2 text-xs text-muted-foreground">{r.message}</td>
                          </tr>
                          {isExpanded && hasSubs && r.pagosAplicados.map((p, j) => (
                            <tr key={`sub-${i}-${j}`} className="bg-green-50 border-t border-green-100">
                              <td className="px-3 py-1.5"></td>
                              <td className="px-3 py-1.5 pl-6 font-mono text-xs text-muted-foreground" colSpan={2}>
                                ↳ {p.invoiceNumber}
                              </td>
                              <td className="px-3 py-1.5 text-xs" colSpan={3}>
                                Aplicado: <strong className="text-green-700">{fmt(p.amountApplied)}</strong>
                              </td>
                              <td className="px-3 py-1.5 text-xs" colSpan={3}>
                                <span className={`px-1.5 py-0.5 rounded text-xs font-medium ${p.invoiceStatus === 'PAID' ? 'bg-green-100 text-green-800' : 'bg-yellow-100 text-yellow-800'}`}>
                                  {p.invoiceStatus === 'PAID' ? 'Pagada' : 'Pago parcial'}
                                </span>
                              </td>
                            </tr>
                          ))}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </div>
      )}
    </div>
  );
}
